/**
 * Hook for the quarantine list — inbound emails held back after a sender-policy failure.
 *
 * A quarantined email has consumed no extraction quota; releasing it is what starts processing.
 */
import { useMutation, useQuery } from '@apollo/client/react';
import { useCallback } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import {
  GetQuarantinedEmailsDocument,
  ReleaseQuarantinedEmailDocument,
  DiscardQuarantinedEmailDocument
} from '@/graphql';

/** Page size for the held-email list; a tenant rarely has more than a handful. */
const PAGE_SIZE = 25;

const REFETCH_QUARANTINE = {
  refetchQueries: [{ query: GetQuarantinedEmailsDocument, variables: { first: PAGE_SIZE } }],
  awaitRefetchQueries: true
};

export function useQuarantinedEmails() {
  const t = useFeatureTranslation('systemSettings.mailbox');
  const { showSuccess, showError } = useToastHelpers();

  const { data, loading, error, fetchMore } = useQuery(GetQuarantinedEmailsDocument, {
    variables: { first: PAGE_SIZE },
    fetchPolicy: 'cache-and-network'
  });

  const [release, { loading: releasing }] = useMutation(ReleaseQuarantinedEmailDocument);
  const [discard, { loading: discarding }] = useMutation(DiscardQuarantinedEmailDocument);

  const connection = data?.quarantinedEmailsConnection;
  const emails = connection?.edges.map(edge => edge.node) ?? [];
  const totalCount = connection?.totalCount ?? 0;
  const hasMore = connection?.pageInfo.hasNextPage ?? false;

  const loadMore = useCallback(async () => {
    if (!hasMore) return;
    await fetchMore({ variables: { first: PAGE_SIZE, after: connection?.pageInfo.endCursor } });
  }, [fetchMore, hasMore, connection?.pageInfo.endCursor]);

  const handleRelease = useCallback(
    async (id: string) => {
      try {
        await release({ variables: { id }, ...REFETCH_QUARANTINE });
        showSuccess(t('quarantine.feedback.releaseSuccess'));
      } catch {
        showError(t('quarantine.feedback.releaseError'));
      }
    },
    [release, showSuccess, showError, t]
  );

  const handleDiscard = useCallback(
    async (id: string) => {
      try {
        await discard({ variables: { id }, ...REFETCH_QUARANTINE });
        showSuccess(t('quarantine.feedback.discardSuccess'));
      } catch {
        showError(t('quarantine.feedback.discardError'));
      }
    },
    [discard, showSuccess, showError, t]
  );

  return {
    t,
    emails,
    totalCount,
    hasMore,
    loadMore,
    loading,
    error,
    handleRelease,
    handleDiscard,
    isBusy: releasing || discarding
  };
}
